import React, { useEffect, useRef, useState } from "react";
import { SunMoon, Play, Download } from "lucide-react";
import { playFreq } from "@/lib/audio";

const BEAT = 0.75;
const num = (b) => (typeof b === "number" ? b : { "½": 0.5, "¼": 0.25, "⅛": 0.125 }[b]);

const VALUES = [
  { uk: "Semibreve", us: "Whole note", beats: 4, desc: "No stem, not coloured in", filled: false, stem: false },
  { uk: "Dotted minim", us: "Dotted half note", beats: 3, desc: "A dot, a stem, not coloured in", filled: false, stem: true, dot: true },
  { uk: "Minim", us: "Half note", beats: 2, desc: "A stem, not coloured in", filled: false, stem: true },
  { uk: "Crotchet", us: "Quarter note", beats: 1, desc: "A stem, coloured in", filled: true, stem: true },
  { uk: "Quaver", us: "Eighth note", beats: "½", desc: "A stem, one tail, coloured in", filled: true, stem: true, tails: 1 },
  { uk: "Semiquaver", us: "Sixteenth note", beats: "¼", desc: "A stem, two tails, coloured in", filled: true, stem: true, tails: 2 },
  { uk: "Demisemiquaver", us: "Thirty-second note", beats: "⅛", desc: "A stem, three tails, coloured in", filled: true, stem: true, tails: 3 },
];

const BeatCounter = ({ v, testId }) => {
  const [beat, setBeat] = useState(-1);
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const n = num(v.beats);
  const dots = Math.max(1, Math.ceil(n));
  const run = () => {
    timers.current.forEach(clearTimeout);
    playFreq(523.25, n * BEAT);
    timers.current = Array.from({ length: dots }, (_, k) => setTimeout(() => setBeat(k), k * BEAT * 1000));
    timers.current.push(setTimeout(() => setBeat(-1), Math.max(n, 1) * BEAT * 1000 + 150));
  };
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
      <button onClick={run} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary/40 px-2 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary hover:text-primary-foreground" data-testid={`${testId}-play`}>
        <Play size={11} /> Hear &amp; count
      </button>
      <span className="flex gap-1" aria-hidden="true">
        {Array.from({ length: dots }).map((_, k) => (
          <span key={k} className={`h-2.5 w-2.5 rounded-full transition-all duration-150 ${k <= beat && beat >= 0 ? "scale-125 bg-red-500" : "bg-muted-foreground/30"}`} data-testid={`${testId}-dot-${k}`} />
        ))}
      </span>
    </div>
  );
};

const Note = ({ v }) => (
  <svg viewBox="0 0 60 80" className="h-20 w-14" aria-hidden="true">
    <ellipse cx="24" cy="60" rx="10" ry="7" transform="rotate(-20 24 60)" fill={v.filled ? "#111" : "#fff"} stroke="#111" strokeWidth="2.6" />
    {v.stem && <line x1="33" y1="58" x2="33" y2="10" stroke="#111" strokeWidth="2.4" />}
    {Array.from({ length: v.tails || 0 }).map((_, k) => <path key={k} d={`M33 ${10 + k * 10} q 4 10 14 16`} fill="none" stroke="#111" strokeWidth="2.6" />)}
    {v.dot && <circle cx="44" cy="60" r="3" fill="#111" />}
  </svg>
);

export const NoteValuesChart = () => (
  <div className="mt-5" data-testid="note-values-chart">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {VALUES.map((v) => (
        <div key={v.uk} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`note-value-${v.uk.toLowerCase().replace(/ /g, "-")}`}>
          <span className="flex shrink-0 items-center justify-center rounded-xl bg-white px-1 shadow-inner"><Note v={v} /></span>
          <div className="min-w-0">
            <p className="font-serif text-lg text-foreground">{v.uk} <span className="text-muted-foreground">//</span> <span className="text-primary">{v.us}</span></p>
            <p className="text-xs text-muted-foreground">{v.desc}</p>
            <p className="mt-0.5 font-mono text-xs text-foreground">{v.beats} beat{num(v.beats) <= 1 ? "" : "s"} / count{num(v.beats) <= 1 ? "" : "s"}</p>
            <BeatCounter v={v} testId={`note-value-${v.uk.toLowerCase().replace(/ /g, "-")}`} />
          </div>
        </div>
      ))}
    </div>
    <a href="/media/note-values-chart.pdf" download className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90" data-testid="note-values-download">
      <Download size={15} /> Download the chart (PDF)
    </a>
    <p className="mt-3 rounded-xl bg-secondary p-3 text-sm text-muted-foreground">
      There are even shorter notes too. To work one out: each extra tail halves the value again. A semiquaver has 2 tails (¼ beat), a demisemiquaver has 3 tails (⅛ beat), and a hemidemisemiquaver // sixty-fourth note has 4 tails (1/16 beat).
    </p>
  </div>
);

export const SheetMusicExample = () => (
  <figure className="mt-5 overflow-hidden rounded-2xl border border-border bg-card" data-testid="sheet-music-example">
    <img src="https://images.unsplash.com/photo-1591025810539-a321000cda85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Real printed sheet music: black notes on white paper"
      className="h-56 w-full object-cover sm:h-72" loading="lazy" />
    <figcaption className="flex items-start gap-3 p-5 text-base text-foreground" data-testid="note-values-theme-tip">
      <SunMoon size={22} className="mt-0.5 shrink-0 text-primary" />
      <span>This is how notes look on real printed sheet music: black notes on white paper. The site starts in dark mode, so the staves here are light on dark. Press <b className="text-foreground">light mode</b> (the sun/moon button at the top) to see the lessons look just like a real page of music.</span>
    </figcaption>
  </figure>
);
